'use client'

import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'

import { useResetClientStorage } from '@/features/auth/hooks/use-reset-client-storage'
import { navigateAfterAuth } from '@/features/auth/lib/post-auth-navigation'
import { getHomePagePath } from '@/features/dashboard/lib/trade-page-path'
import { sanitizeRedirectPath } from '@/lib/auth/redirect'
import { fetchUserAccounts } from '@/service/account.api'
import { authAtom } from '@/state/auth'
import { emitAuthAnalytics } from '@/state/auth-analytics'
import { AuthState } from '@/types/auth'
import { AccountStatus } from '@/types/generated/common/account'

const POPUP_WIDTH = 500
const POPUP_HEIGHT = 650

interface GoogleAuthMessage {
  type: 'google-auth-success'
  authState: AuthState
}

/**
 * Google OAuth를 팝업 창으로 처리하는 훅.
 *
 * 사용법:
 * ```tsx
 * const { loginWithGooglePopup } = useGoogleLoginPopup()
 *
 * // 로그인 성공 시 authAtom 자동 업데이트
 * await loginWithGooglePopup()
 *
 * // 로그인 성공 후 추가 동작이 필요한 경우
 * await loginWithGooglePopup({ onSuccess: () => doSomething() })
 * ```
 */
export function useGoogleLoginPopup() {
  const setAuthState = useSetAtom(authAtom)
  const resetClientStorage = useResetClientStorage()
  const popupRef = useRef<Window | null>(null)
  const resolveRef = useRef<((authState: AuthState) => void) | null>(null)
  const rejectRef = useRef<((reason: Error) => void) | null>(null)
  const redirectUrlRef = useRef<string | undefined>(undefined)

  // postMessage 수신 핸들러
  useEffect(() => {
    const handleMessage = async (event: MessageEvent<GoogleAuthMessage>) => {
      // origin 검증
      const expectedOrigin = window.location.origin
      if (event.origin !== expectedOrigin) return

      if (event.data?.type !== 'google-auth-success') return

      const { authState } = event.data

      // The account has TOTP on: the popup carried a temp token, not a session. Hand off
      // to the MFA form, as a password login that answered requires2fa does.
      if (authState?.requires2fa && authState.tempToken) {
        resetClientStorage()
        setAuthState(authState)
        const target = sanitizeRedirectPath(redirectUrlRef.current ?? window.location.pathname)
        const callbackUrl = target === '/login' ? '/' : target
        rejectRef.current?.(new Error('REQUIRES_2FA'))
        resolveRef.current = null
        rejectRef.current = null
        navigateAfterAuth(`/mfa?callbackUrl=${encodeURIComponent(callbackUrl)}`)
        return
      }

      if (!authState?.isAuthenticated) return

      resetClientStorage()

      // authAtom 업데이트 (쿠키는 서버에서 이미 세팅됨)
      setAuthState(authState)
      if (authState.user?.userId) {
        emitAuthAnalytics({
          method: 'google',
          userId: authState.user.userId,
          kind: authState.isNewUser ? 'signup' : 'login',
        })
      }

      // /login 페이지에서 성공 시 리다이렉트 처리
      if (window.location.pathname === '/login') {
        if (authState.isNewUser) {
          navigateAfterAuth('/onboarding')
        } else {
          try {
            const accountsData = await fetchUserAccounts()
            const hasActiveAccount = accountsData.accounts.some(
              (account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE
            )
            navigateAfterAuth(hasActiveAccount ? getHomePagePath() : '/onboarding')
          } catch (error) {
            console.error('Failed to fetch accounts after Google login:', error)
            navigateAfterAuth('/onboarding')
          }
        }
      }

      // Promise resolve
      resolveRef.current?.(authState)
      resolveRef.current = null
      rejectRef.current = null
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [resetClientStorage, setAuthState])

  const loginWithGooglePopup = useCallback(
    (options?: { onSuccess?: (authState: AuthState) => void; redirectUrl?: string }) => {
      return new Promise<AuthState>((resolve, reject) => {
        // 이미 열린 팝업이 있으면 포커스
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.focus()
          return
        }

        const redirectUrl = options?.redirectUrl ?? window.location.pathname
        redirectUrlRef.current = redirectUrl
        const params = new URLSearchParams({
          redirect: redirectUrl,
          popup: 'true',
        })

        // 팝업 창 중앙 정렬
        const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2
        const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2
        const features = [
          `width=${POPUP_WIDTH}`,
          `height=${POPUP_HEIGHT}`,
          `left=${left}`,
          `top=${top}`,
          'resizable=yes',
          'scrollbars=yes',
        ].join(',')

        const popup = window.open(`/api/auth/google?${params}`, 'google-login', features)

        if (!popup) {
          // 팝업 차단된 경우 기존 리다이렉트 방식으로 폴백
          reject(new Error('POPUP_BLOCKED'))
          return
        }

        popupRef.current = popup

        resolveRef.current = (authState: AuthState) => {
          options?.onSuccess?.(authState)
          resolve(authState)
        }
        rejectRef.current = reject

        // 팝업이 수동으로 닫힌 경우 감지
        const closedTimer = setInterval(() => {
          if (popup.closed) {
            clearInterval(closedTimer)
            // postMessage 이벤트가 먼저 처리될 수 있도록 잠시 대기 후 취소 여부 확인
            // (인증 성공 후 팝업이 빠르게 닫히는 경우 race condition 방지)
            setTimeout(() => {
              if (resolveRef.current) {
                reject(new Error('POPUP_CLOSED'))
                resolveRef.current = null
                rejectRef.current = null
              }
            }, 100)
          }
        }, 500)
      })
    },
    []
  )

  return { loginWithGooglePopup }
}
